import { BOARD_VIEW_TYPE } from '@/features/tasks/public';
import type { BoardViewType } from '@/features/tasks/public';
import { FIELD_TYPE } from '@/features/custom-fields/public';
import type { FieldType } from '@/features/custom-fields/public';
import type { Priority } from '@/shared/lib/priority';
import type {
  CreateProjectTemplatePayload,
  ProjectTemplate,
  TemplateBoard,
  TemplateCustomField,
  TemplateGroup,
  TemplateStatus,
  TemplateTag,
  TemplateTask,
  UpdateTemplateStructurePayload,
} from '../types';

/** Wire enum values — see `docs/project-templates-frontend-integration-guide.md` §3/§4 in the backend repo. */
type WireViewType =
  | 'List'
  | 'Kanban'
  | 'Calendar'
  | 'Gantt'
  | 'Documents'
  | 'Dashboard'
  | 'Activity'
  | 'Timeline'
  | 'Whiteboard';

type WireFieldType =
  | 'Text'
  | 'Number'
  | 'Date'
  | 'Select'
  | 'MultiSelect'
  | 'Checkbox'
  | 'Users'
  | 'Email'
  | 'Url'
  | 'Phone'
  | 'Currency'
  | 'Files'
  | 'Signature';

type WirePriority = 'None' | 'Low' | 'Medium' | 'High' | 'Urgent';

interface WireTask {
  title: string;
  priority: WirePriority;
}

interface WireGroup {
  name: string;
  order: number;
  tasks: WireTask[];
}

interface WireStatus {
  name: string;
  color: string;
  type: TemplateStatus['type'];
  order: number;
}

interface WireView {
  type: WireViewType;
  name: string;
  isDefault: boolean;
}

interface WireBoard {
  name: string;
  description: string | null;
  order: number;
  statuses: WireStatus[];
  views: WireView[];
  groups: WireGroup[];
  ungroupedTasks: WireTask[];
}

interface WireCustomField {
  name: string;
  type: WireFieldType;
  options: string[] | null;
}

interface WireTag {
  name: string;
  color: string;
}

export interface WireTemplateStructure {
  boards: WireBoard[];
  customFields: WireCustomField[];
  tags: WireTag[];
}

const VIEW_TYPE_TO_WIRE: Record<BoardViewType, WireViewType> = {
  [BOARD_VIEW_TYPE.List]: 'List',
  [BOARD_VIEW_TYPE.Kanban]: 'Kanban',
  [BOARD_VIEW_TYPE.Calendar]: 'Calendar',
  [BOARD_VIEW_TYPE.Gantt]: 'Gantt',
  [BOARD_VIEW_TYPE.Documents]: 'Documents',
  [BOARD_VIEW_TYPE.Dashboard]: 'Dashboard',
  [BOARD_VIEW_TYPE.Activity]: 'Activity',
  [BOARD_VIEW_TYPE.Timeline]: 'Timeline',
  [BOARD_VIEW_TYPE.Whiteboard]: 'Whiteboard',
};

const WIRE_TO_VIEW_TYPE: Record<WireViewType, BoardViewType> = {
  List: BOARD_VIEW_TYPE.List,
  Kanban: BOARD_VIEW_TYPE.Kanban,
  Calendar: BOARD_VIEW_TYPE.Calendar,
  Gantt: BOARD_VIEW_TYPE.Gantt,
  Documents: BOARD_VIEW_TYPE.Documents,
  Dashboard: BOARD_VIEW_TYPE.Dashboard,
  Activity: BOARD_VIEW_TYPE.Activity,
  Timeline: BOARD_VIEW_TYPE.Timeline,
  Whiteboard: BOARD_VIEW_TYPE.Whiteboard,
};

const FIELD_TYPE_TO_WIRE: Record<FieldType, WireFieldType> = {
  [FIELD_TYPE.Text]: 'Text',
  [FIELD_TYPE.Number]: 'Number',
  [FIELD_TYPE.Date]: 'Date',
  [FIELD_TYPE.Select]: 'Select',
  [FIELD_TYPE.MultiSelect]: 'MultiSelect',
  [FIELD_TYPE.Checkbox]: 'Checkbox',
  [FIELD_TYPE.Users]: 'Users',
  [FIELD_TYPE.Email]: 'Email',
  [FIELD_TYPE.Url]: 'Url',
  [FIELD_TYPE.Phone]: 'Phone',
  [FIELD_TYPE.Currency]: 'Currency',
  [FIELD_TYPE.Files]: 'Files',
  [FIELD_TYPE.Signature]: 'Signature',
};

const WIRE_TO_FIELD_TYPE: Record<WireFieldType, FieldType> = {
  Text: FIELD_TYPE.Text,
  Number: FIELD_TYPE.Number,
  Date: FIELD_TYPE.Date,
  Select: FIELD_TYPE.Select,
  MultiSelect: FIELD_TYPE.MultiSelect,
  Checkbox: FIELD_TYPE.Checkbox,
  Users: FIELD_TYPE.Users,
  Email: FIELD_TYPE.Email,
  Url: FIELD_TYPE.Url,
  Phone: FIELD_TYPE.Phone,
  Currency: FIELD_TYPE.Currency,
  Files: FIELD_TYPE.Files,
  Signature: FIELD_TYPE.Signature,
};

const PRIORITY_TO_WIRE: Record<Priority, WirePriority> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/** `undefined` (no priority selected) round-trips as the backend's `"None"`. */
function priorityToWire(priority: Priority | undefined): WirePriority {
  return priority ? PRIORITY_TO_WIRE[priority] : 'None';
}

function wireToPriority(priority: WirePriority): Priority | undefined {
  switch (priority) {
    case 'Urgent':
      return 'urgent';
    case 'High':
      return 'high';
    case 'Medium':
      return 'medium';
    case 'Low':
      return 'low';
    default:
      return undefined;
  }
}

function taskToWire(task: TemplateTask): WireTask {
  return { title: task.title, priority: priorityToWire(task.priority) };
}

function wireToTask(task: WireTask): TemplateTask {
  return { title: task.title, priority: wireToPriority(task.priority) };
}

function groupToWire(group: TemplateGroup): WireGroup {
  return { name: group.name, order: group.order, tasks: group.tasks.map(taskToWire) };
}

function wireToGroup(group: WireGroup): TemplateGroup {
  return { name: group.name, order: group.order, tasks: group.tasks.map(wireToTask) };
}

function boardToWire(board: TemplateBoard): WireBoard {
  return {
    name: board.name,
    description: board.description,
    order: board.order,
    statuses: board.statuses.map((status) => ({ ...status })),
    views: board.views.map((view) => ({
      type: VIEW_TYPE_TO_WIRE[view.type],
      name: view.name,
      isDefault: view.isDefault,
    })),
    groups: board.groups.map(groupToWire),
    ungroupedTasks: board.ungroupedTasks.map(taskToWire),
  };
}

function wireToBoard(board: WireBoard): TemplateBoard {
  return {
    name: board.name,
    description: board.description,
    order: board.order,
    statuses: board.statuses.map((status) => ({ ...status })),
    views: board.views.map((view) => ({
      type: WIRE_TO_VIEW_TYPE[view.type],
      name: view.name,
      isDefault: view.isDefault,
    })),
    groups: board.groups.map(wireToGroup),
    ungroupedTasks: board.ungroupedTasks.map(wireToTask),
  };
}

function customFieldToWire(field: TemplateCustomField): WireCustomField {
  return { name: field.name, type: FIELD_TYPE_TO_WIRE[field.type], options: field.options };
}

function wireToCustomField(field: WireCustomField): TemplateCustomField {
  return { name: field.name, type: WIRE_TO_FIELD_TYPE[field.type], options: field.options };
}

function tagToWire(tag: TemplateTag): WireTag {
  return { ...tag };
}

function wireToTag(tag: WireTag): TemplateTag {
  return { ...tag };
}

/** Builds the `structureJson` string sent to `POST/PUT .../structure` from domain payload fields. */
export function encodeStructureJson(
  payload: Pick<
    CreateProjectTemplatePayload | UpdateTemplateStructurePayload,
    'boards' | 'customFields' | 'tags'
  >,
): string {
  const structure: WireTemplateStructure = {
    boards: payload.boards.map(boardToWire),
    customFields: payload.customFields.map(customFieldToWire),
    tags: payload.tags.map(tagToWire),
  };
  return JSON.stringify(structure);
}

/** Parses a `structureJson` string into the domain `boards`/`customFields`/`tags` shape. */
export function decodeStructureJson(
  structureJson: string,
): Pick<ProjectTemplate, 'boards' | 'customFields' | 'tags'> {
  const structure = JSON.parse(structureJson) as WireTemplateStructure;
  return {
    boards: structure.boards.map(wireToBoard),
    customFields: structure.customFields.map(wireToCustomField),
    tags: structure.tags.map(wireToTag),
  };
}
